import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Shield, Globe, Users, Award, ArrowRight } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';
import { useSEO } from '../hooks/useSEO';

const AboutPage = () => {
  const { t, language } = useTranslation();
  useSEO('about');
  
  const stats = [
    { number: "300M+", label: t('aboutPage.stats.ipAddresses') },
    { number: "200+", label: t('aboutPage.stats.countries') },
    { number: "10K+", label: t('aboutPage.stats.happyCustomers') },
    { number: "99.9%", label: t('aboutPage.stats.uptimeSLA') }
  ];

  const values = [
    {
      icon: Shield,
      title: t('aboutPage.values.securityFirst.title'),
      description: t('aboutPage.values.securityFirst.description')
    },
    {
      icon: Globe,
      title: t('aboutPage.values.globalReach.title'),
      description: t('aboutPage.values.globalReach.description')
    },
    {
      icon: Users,
      title: t('aboutPage.values.customerSuccess.title'),
      description: t('aboutPage.values.customerSuccess.description')
    },
    {
      icon: Award,
      title: t('aboutPage.values.innovation.title'),
      description: t('aboutPage.values.innovation.description')
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      <main className="">
        {/* Hero Section */}
        <section className="py-24 bg-gradient-to-b from-slate-950 to-slate-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              {language === 'zh' ? (
                <span className="text-cyan-400">{t('aboutPage.title')}</span>
              ) : (
                <>About <span className="text-cyan-400">IP9</span></>
              )}
            </h1>
            <p className="text-xl text-slate-400 max-w-3xl mx-auto mb-8">
              {t('aboutPage.heroDescription')}
            </p>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-16 bg-slate-900/50 border-y border-slate-800">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2">
                    {stat.number}
                  </div>
                  <div className="text-slate-400 text-sm md:text-base font-medium">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Story Section */}
        <section className="py-24 bg-slate-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">{t('aboutPage.story.title')}</h2>
              <p className="text-xl text-slate-400">
                {t('aboutPage.story.subtitle')}
              </p>
            </div>

            <div className="prose prose-invert prose-slate max-w-none">
              <div className="bg-slate-950/50 backdrop-blur-sm border border-slate-800 rounded-2xl p-8 mb-8">
                <p className="text-lg text-slate-300 leading-relaxed mb-6">
                  {t('aboutPage.story.paragraph1')}
                </p>
                
                <p className="text-lg text-slate-300 leading-relaxed mb-6">
                  {t('aboutPage.story.paragraph2')}
                </p>
                
                <p className="text-lg text-slate-300 leading-relaxed">
                  {t('aboutPage.story.paragraph3')}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-24 bg-slate-950">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">{t('aboutPage.values.title')}</h2>
              <p className="text-xl text-slate-400 max-w-3xl mx-auto">
                {t('aboutPage.values.subtitle')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {values.map((value, index) => {
                const IconComponent = value.icon;
                
                return (
                  <div
                    key={index}
                    className="bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-2xl p-8 hover:border-slate-700 hover:bg-slate-900/70 transition-all duration-300"
                  >
                    <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-2xl mb-6">
                      <IconComponent className="w-8 h-8 text-cyan-400" />
                    </div>
                    
                    <h3 className="text-xl font-bold text-white mb-4">
                      {value.title}
                    </h3>
                    
                    <p className="text-slate-400 leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>


        {/* CTA Section */}
        <section className="py-24 bg-slate-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-3xl p-12">
              <h2 className="text-3xl font-bold text-white mb-6">{t('aboutPage.cta.title')}</h2>
              <p className="text-xl text-slate-400 mb-8 max-w-2xl mx-auto">
                {t('aboutPage.cta.subtitle')}
              </p>
              <div className="space-x-4">
                <a 
                  href="https://dashboard.ip9.io" 
                  className="inline-block"
                >
                  <Button size="lg" className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold px-8 py-4">
                    {t('aboutPage.cta.startFreeTrial')}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </a>
                <a 
                  href="mailto:sales@ip9.io"
                  className="inline-block"
                >
                  <Button size="lg" variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white px-8 py-4">
                    {t('aboutPage.cta.contactSales')}
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default AboutPage;